import { useState, useMemo, useCallback } from 'react';
import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { Box, Typography, Pagination, useMediaQuery, useTheme, Skeleton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReorderIcon from '@mui/icons-material/Reorder';
import { ProductCatalogEditPanel } from '@components/dialogs/ProductCatalogEditDialog';
import { OrderProductCatalogsPanel } from '@components/dialogs/OrderProductCatalogsDialog';
import { useProductCatalogsQuery } from '@features/redux/apis/productCatalog';
import { useAlert } from '@hooks/useAlert';
import { useScrollablePagination } from '@hooks/useScrollablePagination';

// Design system
import { colors, radii, typography as typographyTokens, shadows } from '@components/design-system/tokens';
import { DataTable, TableCard, Button } from '@components/design-system';
import type { DataTableColumn } from '@components/design-system/components/Table/DataTable';

import type { ProductCatalog } from '@/types/productCatalog';
import { BillingType } from '@consts/company';

const PAGE_SIZE = 50;
const MOBILE_PAGE_SIZE = 12;
const PRODUCT_LINE_ORDER = ['Original', 'Fusion', 'Style Skins'];

// Product line colors for visual grouping
const PRODUCT_LINE_COLORS: Record<string, { bg: string; text: string }> = {
    Original: { bg: colors.primary[50], text: colors.primary[700] },
    Fusion: { bg: colors.success.light, text: colors.success.dark },
    'Style Skins': { bg: colors.warning.light, text: colors.warning.dark },
};

const getCutPlanDescription = (cutPlanID: number): string => {
    return cutPlanID === BillingType.NoCutFee ? 'Cut Fee Included' : 'Cut Fee Not Included';
};

// Mobile Card Component
interface MobileProductCatalogCardProps {
    data: ProductCatalog;
    onEdit: (product: ProductCatalog) => void;
}

const MobileProductCatalogCard = ({ data, onEdit }: MobileProductCatalogCardProps) => {
    const lineColor = PRODUCT_LINE_COLORS[data.ProductLine] || { bg: colors.neutral[100], text: colors.neutral[700] };

    return (
        <Box
            sx={{
                backgroundColor: colors.neutral[0],
                borderRadius: `${radii.lg}px`,
                border: `1px solid ${colors.neutral[200]}`,
                p: 2,
                mb: 1.5,
                boxShadow: shadows.card,
            }}>
            {/* Header with Short Code and Product Line */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.75,
                            borderRadius: `${radii.md}px`,
                            backgroundColor: colors.neutral[900],
                            color: colors.neutral[0],
                            fontFamily: typographyTokens.fontFamily.mono,
                            fontWeight: typographyTokens.fontWeight.bold,
                            fontSize: typographyTokens.fontSize.md,
                            letterSpacing: '0.1em',
                        }}>
                        {data.ShortCode}
                    </Box>
                    <Box
                        sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: `${radii.sm}px`,
                            backgroundColor: lineColor.bg,
                            color: lineColor.text,
                            fontSize: typographyTokens.fontSize.xs,
                            fontWeight: typographyTokens.fontWeight.medium,
                        }}>
                        {data.ProductLine}
                    </Box>
                </Box>
                <Button variant='secondary' size='small' onClick={() => onEdit(data)}>
                    Edit
                </Button>
            </Box>

            {/* Product Name */}
            <Typography
                sx={{
                    fontSize: typographyTokens.fontSize.base,
                    fontWeight: typographyTokens.fontWeight.semibold,
                    color: colors.neutral[900],
                    mb: 0.5,
                }}>
                {data.ProductName}
            </Typography>

            {/* Invoice Name */}
            <Typography
                sx={{
                    fontSize: typographyTokens.fontSize.sm,
                    color: colors.neutral[600],
                    mb: 1.5,
                }}>
                {data.InvoiceName}
            </Typography>

            {/* Details Grid */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[400],
                            minWidth: 80,
                        }}>
                        Product SKU
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[700],
                            fontFamily: typographyTokens.fontFamily.mono,
                        }}>
                        {data.ProductSKU}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[400],
                            minWidth: 80,
                        }}>
                        Success SKU
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[700],
                            fontFamily: typographyTokens.fontFamily.mono,
                        }}>
                        {data.SuccessCutSKU}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[400],
                            minWidth: 80,
                        }}>
                        Warranty SKU
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[700],
                            fontFamily: typographyTokens.fontFamily.mono,
                        }}>
                        {data.WarrantyCutSKU}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[400],
                            minWidth: 80,
                        }}>
                        Cut Plan
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[700],
                        }}>
                        {getCutPlanDescription(data.CutPlanID)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const MobileProductCatalogCardSkeleton = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant='rounded' width={48} height={32} />
                <Skeleton variant='rounded' width={60} height={20} />
            </Box>
            <Skeleton variant='rounded' width={50} height={32} />
        </Box>
        <Skeleton variant='text' width='70%' height={24} />
        <Skeleton variant='text' width='90%' height={20} />
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Skeleton variant='text' width='60%' height={16} />
            <Skeleton variant='text' width='50%' height={16} />
            <Skeleton variant='text' width='55%' height={16} />
        </Box>
    </Box>
);

// Selection state type for edit/order panel
type SelectionState = {
    type: 'edit' | 'create' | 'order' | null;
    product?: ProductCatalog;
};

export const ProductCatalogManagement = () => {
    const { data: productCatalogs = [], isLoading } = useProductCatalogsQuery({});
    const { setAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { scrollContainerRef, scrollToTop } = useScrollablePagination();

    const [page, setPage] = useState(1);
    const [selection, setSelection] = useState<SelectionState>({ type: null });

    const pageSize = isMobile ? MOBILE_PAGE_SIZE : PAGE_SIZE;

    const sortedProducts = useMemo(() => {
        return [...productCatalogs].sort((a, b) => {
            const lineIndexA = PRODUCT_LINE_ORDER.indexOf(a.ProductLine);
            const lineIndexB = PRODUCT_LINE_ORDER.indexOf(b.ProductLine);
            const sortedLineA = lineIndexA === -1 ? Infinity : lineIndexA;
            const sortedLineB = lineIndexB === -1 ? Infinity : lineIndexB;

            if (sortedLineA !== sortedLineB) return sortedLineA - sortedLineB;

            const sortOrderA = a.SortOrder ?? Infinity;
            const sortOrderB = b.SortOrder ?? Infinity;
            return sortOrderA - sortOrderB;
        });
    }, [productCatalogs]);

    // Paginated products for current page
    const paginatedProducts = useMemo(() => {
        const start = (page - 1) * pageSize;
        return sortedProducts.slice(start, start + pageSize);
    }, [sortedProducts, page, pageSize]);

    const totalPages = Math.ceil(sortedProducts.length / pageSize);
    const infoText =
        sortedProducts.length > 0
            ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, sortedProducts.length)} of ${sortedProducts.length} products`
            : 'No products found';

    const handlePageChange = useCallback(
        (newPage: number) => {
            setPage(newPage);
            scrollToTop();
        },
        [scrollToTop]
    );

    const openEditPanel = (product?: ProductCatalog) => {
        setSelection({ type: product ? 'edit' : 'create', product });
    };

    const closeEditPanel = () => {
        setSelection({ type: null });
    };

    // Desktop columns using design system
    const columns = useMemo<DataTableColumn<ProductCatalog>[]>(() => {
        return [
            {
                field: 'ShortCode',
                headerName: 'Short Code',
                width: 100,
                renderCell: ({ row }) => (
                    <Box
                        sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: `${radii.sm}px`,
                            backgroundColor: colors.neutral[900],
                            color: colors.neutral[0],
                            fontFamily: typographyTokens.fontFamily.mono,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            fontSize: typographyTokens.fontSize.sm,
                            letterSpacing: '0.1em',
                        }}>
                        {row.ShortCode}
                    </Box>
                ),
            },
            {
                field: 'ProductSKU',
                headerName: 'Product SKU',
                width: 150,
                renderCell: ({ row }) => (
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.sm,
                            fontFamily: typographyTokens.fontFamily.mono,
                            color: colors.neutral[700],
                        }}>
                        {row.ProductSKU}
                    </Typography>
                ),
            },
            {
                field: 'ProductLine',
                headerName: 'Product Line',
                width: 130,
                renderCell: ({ row }) => {
                    const lineColor = PRODUCT_LINE_COLORS[row.ProductLine] || {
                        bg: colors.neutral[100],
                        text: colors.neutral[700],
                    };
                    return (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: `${radii.sm}px`,
                                backgroundColor: lineColor.bg,
                                color: lineColor.text,
                                fontSize: typographyTokens.fontSize.xs,
                                fontWeight: typographyTokens.fontWeight.medium,
                            }}>
                            {row.ProductLine}
                        </Box>
                    );
                },
            },
            {
                field: 'ProductName',
                headerName: 'Product Name',
                minWidth: 150,
                flex: 2,
            },
            {
                field: 'InvoiceName',
                headerName: 'Invoice Name',
                width: 250,
                flex: 1,
            },
            {
                field: 'CutProfile',
                headerName: 'Cut Profile',
                width: 160,
                renderCell: ({ row }) => {
                    if (row.CutProfile == null) return '—';
                    const sku = row.CutProfileSKU ? ` (${row.CutProfileSKU})` : '';
                    return `${row.CutProfile}${sku}`;
                },
            },
            {
                field: 'SuccessCutSKU',
                headerName: 'Success SKU',
                width: 140,
                renderCell: ({ row }) => (
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.sm,
                            fontFamily: typographyTokens.fontFamily.mono,
                            color: colors.neutral[600],
                        }}>
                        {row.SuccessCutSKU}
                    </Typography>
                ),
            },
            {
                field: 'WarrantyCutSKU',
                headerName: 'Warranty SKU',
                width: 140,
                renderCell: ({ row }) => (
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.sm,
                            fontFamily: typographyTokens.fontFamily.mono,
                            color: colors.neutral[600],
                        }}>
                        {row.WarrantyCutSKU}
                    </Typography>
                ),
            },
            {
                field: 'CutPlanID',
                headerName: 'Cut Plan',
                width: 160,
                renderCell: ({ row }) => getCutPlanDescription(row.CutPlanID),
            },
            {
                field: 'JobName',
                headerName: 'Job Name',
                width: 130,
            },
            {
                field: 'SortOrder',
                headerName: 'Order',
                width: 70,
                renderCell: ({ row }) => row.SortOrder + 1,
            },
            {
                field: 'Actions',
                headerName: 'Actions',
                width: 80,
                renderCell: ({ row }) => (
                    <Button variant='secondary' size='small' onClick={() => openEditPanel(row)}>
                        Edit
                    </Button>
                ),
            },
        ];
    }, []);

    // Mobile View
    if (isMobile) {
        return (
            <BasePage noPadding>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                    {/* Show Edit/Order Panel or List View */}
                    {selection.type === 'order' ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                            }}>
                            <OrderProductCatalogsPanel onClose={closeEditPanel} />
                        </Box>
                    ) : selection.type ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                            }}>
                            <ProductCatalogEditPanel
                                productCatalog={selection.product}
                                existingProducts={productCatalogs}
                                onClose={closeEditPanel}
                                onSave={() => {
                                    setAlert('Product catalog saved successfully!', 'success', 5000);
                                    closeEditPanel();
                                }}
                            />
                        </Box>
                    ) : (
                        <>
                            {/* Mobile Header - Row 1: Title + Add Button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    px: 2,
                                    pt: 2,
                                    pb: 1.5,
                                    backgroundColor: colors.neutral[0],
                                    borderBottom: `1px solid ${colors.neutral[200]}`,
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: '1.125rem',
                                        fontWeight: 700,
                                        color: colors.neutral[900],
                                    }}>
                                    Product Catalog
                                </Typography>
                                <Button variant='primary' size='small' startIcon={<AddIcon />} onClick={() => openEditPanel()}>
                                    Add
                                </Button>
                            </Box>

                            {/* Mobile Header - Row 2: Info + Order Button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1.5,
                                    backgroundColor: colors.neutral[0],
                                    borderBottom: `1px solid ${colors.neutral[200]}`,
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.sm,
                                        color: colors.neutral[600],
                                    }}>
                                    {sortedProducts.length} products
                                </Typography>
                                <Button
                                    variant='secondary'
                                    size='small'
                                    startIcon={<ReorderIcon />}
                                    onClick={() => setSelection({ type: 'order' })}>
                                    Reorder
                                </Button>
                            </Box>

                            {/* Cards Container */}
                            <Box
                                ref={scrollContainerRef}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: 'auto',
                                    p: 1.5,
                                    backgroundColor: colors.neutral[50],
                                }}>
                                {isLoading ? (
                                    Array.from({ length: 5 }).map((_, i) => <MobileProductCatalogCardSkeleton key={i} />)
                                ) : paginatedProducts.length === 0 ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            color: colors.neutral[500],
                                        }}>
                                        <Typography>No products found</Typography>
                                    </Box>
                                ) : (
                                    paginatedProducts.map((product) => (
                                        <MobileProductCatalogCard
                                            key={product.ProductSKU}
                                            data={product}
                                            onEdit={openEditPanel}
                                        />
                                    ))
                                )}
                            </Box>

                            {/* Pagination - Fixed at bottom */}
                            {totalPages > 1 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        py: 1.5,
                                        backgroundColor: colors.neutral[0],
                                        borderTop: `1px solid ${colors.neutral[200]}`,
                                    }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, newPage) => handlePageChange(newPage)}
                                        color='primary'
                                        size='small'
                                        siblingCount={0}
                                        boundaryCount={1}
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                borderRadius: `${radii.md}px`,
                                                minWidth: 32,
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>

            </BasePage>
        );
    }

    // Desktop View - Using design system components
    return (
        <BasePage noPadding>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}>
                <BreadcrumbSubBar />

                {/* Show Edit/Order Panel or List View */}
                {selection.type === 'order' ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                        }}>
                        <OrderProductCatalogsPanel onClose={closeEditPanel} />
                    </Box>
                ) : selection.type ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                        }}>
                        <ProductCatalogEditPanel
                            productCatalog={selection.product}
                            existingProducts={productCatalogs}
                            onClose={closeEditPanel}
                            onSave={() => {
                                setAlert('Product catalog saved successfully!', 'success', 5000);
                                closeEditPanel();
                            }}
                        />
                    </Box>
                ) : (
                    <>
                        {/* Desktop Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                px: 3,
                                py: 2,
                                backgroundColor: colors.neutral[0],
                                borderBottom: `1px solid ${colors.neutral[200]}`,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: colors.neutral[900],
                                }}>
                                Product Catalog Management
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button variant='secondary' startIcon={<ReorderIcon />} onClick={() => setSelection({ type: 'order' })}>
                                    Manage Order
                                </Button>
                                <Button variant='primary' startIcon={<AddIcon />} onClick={() => openEditPanel()}>
                                    Add Product
                                </Button>
                            </Box>
                        </Box>

                        {/* Table */}
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                p: 2,
                                backgroundColor: colors.neutral[100],
                            }}>
                            <TableCard
                                title='Products'
                                sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                                <DataTable
                                    rows={paginatedProducts}
                                    columns={columns}
                                    getRowId={(row) => row.ProductSKU}
                                    totalRows={sortedProducts.length}
                                    page={page}
                                    pageSize={pageSize}
                                    onPageChange={setPage}
                                    loading={isLoading}
                                    height='100%'
                                    infoText={infoText}
                                />
                            </TableCard>
                        </Box>
                    </>
                )}
            </Box>
        </BasePage>
    );
};

// Keep old export name for backwards compatibility
export const MaterialProductManagement = ProductCatalogManagement;
